import {
  addMonths,
  dayNumber,
  daysInMonth,
  isValidDate,
  isWorkday,
  type Time,
  weekday,
  weekStart,
  ymd,
} from "./calendar";
import type { DateRef, Env } from "./types";

export type Unit = "minute" | "hour" | "day" | "week" | "month" | "year";

export const TIME_OF_DAY = {
  morning: { h: 9, m: 0 },
  afternoon: { h: 12, m: 0 },
  evening: { h: 19, m: 0 },
  night: { h: 22, m: 0 },
} satisfies Record<string, Time>;

export const rel = (dn: number): DateRef => ({ dn, absolute: false });
export const abs = (dn: number): DateRef => ({ dn, absolute: true });

/** Next date strictly after today on ISO weekday `wd`. */
export function upcomingWeekday(env: Env, wd: number): number {
  return env.today + (((wd - weekday(env.today) + 6) % 7) + 1);
}

/** Weekday `wd` in the week `weeksAhead` from now (weeks start on `startDay`). */
export function weekdayInWeek(env: Env, wd: number, weeksAhead: number): number {
  return weekStart(env.today, env.startDay) + 7 * weeksAhead + ((wd - env.startDay + 7) % 7);
}

/** "this friday": this week's day, or the upcoming one if it has already passed. */
export function thisWeekday(env: Env, wd: number): number {
  const d = weekdayInWeek(env, wd, 0);
  return d >= env.today ? d : upcomingWeekday(env, wd);
}

export const nextWeekday = (env: Env, wd: number) => weekdayInWeek(env, wd, 1);

/** Month/day without a year: the next occurrence on or after today (skipping Feb 29 in non-leap years). */
export function monthDay(env: Env, m: number, d: number, year?: number): DateRef | null {
  if (year !== undefined) return isValidDate(year, m, d) ? abs(dayNumber(year, m, d)) : null;
  if (!isValidDate(2000, m, d)) return null;
  const { y } = ymd(env.today);
  for (let i = 0; i < 9; i++) {
    if (isValidDate(y + i, m, d)) {
      const dn = dayNumber(y + i, m, d);
      if (dn >= env.today) return abs(dn);
    }
  }
  return null;
}

/** Day of month without a month ("27th"): this month if still ahead, otherwise the next month that has it. */
export function dayOfMonth(env: Env, d: number): DateRef | null {
  if (d < 1 || d > 31) return null;
  const { y, m } = ymd(env.today);
  for (let i = 0; i < 13; i++) {
    const total = y * 12 + m - 1 + i;
    const yy = Math.floor(total / 12);
    const mm = (total % 12) + 1;
    if (d <= daysInMonth(yy, mm)) {
      const dn = dayNumber(yy, mm, d);
      if (dn >= env.today) return rel(dn);
    }
  }
  return null;
}

export function expandYear(y: number): number {
  return y < 100 ? 2000 + y : y;
}

export const endOfWeek = (env: Env) => weekStart(env.today, env.startDay) + 6;

export function endOfMonth(dn: number): number {
  const { y, m } = ymd(dn);
  return dayNumber(y, m, daysInMonth(y, m));
}

export function firstOfNextMonth(dn: number): number {
  const { y, m } = ymd(dn);
  return m === 12 ? dayNumber(y + 1, 1, 1) : dayNumber(y, m + 1, 1);
}

export function nextWorkdayAfter(dn: number): number {
  let d = dn + 1;
  while (!isWorkday(d)) d++;
  return d;
}

/** "this weekend": the coming weekend start day (today if it's that day). */
export function thisWeekend(env: Env): number {
  return env.today + ((env.weekendStartDay - weekday(env.today) + 7) % 7);
}

/** Adds an amount of a unit to a date (+ optional time); minutes/hours carry over into days. */
export function addUnit(dn: number, minutes: number, unit: Unit, n: number): { dn: number; minutes: number } {
  switch (unit) {
    case "minute":
    case "hour": {
      const total = dn * 1440 + minutes + n * (unit === "hour" ? 60 : 1);
      return { dn: Math.floor(total / 1440), minutes: total - Math.floor(total / 1440) * 1440 };
    }
    case "day":
      return { dn: dn + n, minutes };
    case "week":
      return { dn: dn + 7 * n, minutes };
    case "month":
      return { dn: addMonths(dn, n), minutes };
    case "year":
      return { dn: addMonths(dn, 12 * n), minutes };
  }
}

export const toTime = (minutes: number): Time => ({ h: Math.floor(minutes / 60), m: minutes % 60 });

/** Validates an hour/minute pair, applying an am/pm marker. */
export function clock(h: number, m: number, meridiem?: "am" | "pm"): Time | null {
  if (m < 0 || m > 59) return null;
  if (meridiem) {
    if (h < 1 || h > 12) return null;
    return { h: (h % 12) + (meridiem === "pm" ? 12 : 0), m };
  }
  if (h < 0 || h > 24) return null;
  if (h === 24) return m === 0 ? { h: 0, m: 0 } : null;
  return { h, m };
}
